import { inject, Injectable } from '@angular/core';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import {
  type DatasetWorkflowNodeBase,
  type WorkflowNodeResultMessages,
  type WorkflowNodeWithRowId,
} from '@blaze-shared/workflow-domain';

import { getRowId } from './dataset-row-id';
import { type NodeInput, normalizeNodeInput } from './node-input-rows';

export interface NodeResultMessages {
  errorMessage: string;
  successMessage: string;
}

/** A node carrying result messages, plus whatever identifies the row those messages describe. */
type NodeWithResultMessages = WorkflowNodeResultMessages &
  WorkflowNodeWithRowId &
  DatasetWorkflowNodeBase;

/**
 * Evaluates the messages a node shows the user once it succeeds or fails.
 *
 * Both are author-written expressions, so they are evaluated against the row the node acted on —
 * which for a current-row node is the row its id resolves to, not simply the first one in.
 */
@Injectable({ providedIn: 'root' })
export class NodeResultMessagesService {
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  getErrorAndSuccessMessage(
    node: NodeWithResultMessages,
    nodeInput: NodeInput,
    ctx: RuntimeContext,
  ): NodeResultMessages {
    const rows = normalizeNodeInput(nodeInput);
    let messageContextRow: DatasetRow | undefined = rows[0];
    let context = ctx;
    if (node.currentRow) {
      context = this.iss.updateRuntimeContext(ctx, nodeInput);
      const rowId = getRowId(this.schemaService.getIdColumnName(node.datasetId), context);
      messageContextRow = rows?.find((row) => row?.id === rowId);
    }
    context = this.iss.updateRuntimeContext(ctx, messageContextRow);
    // Messages read a single value per name, so an array value evaluates against its first entry.
    for (const key in context) {
      if (Array.isArray(context[key])) {
        context[key] = ctx[key][0];
      }
    }
    return {
      errorMessage: this.iss.eval(node.errorMessage, context) ?? node.errorMessage,
      successMessage: this.iss.eval(node.successMessage, context) ?? node.successMessage,
    };
  }
}
